import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

const escapeHTML = (value) => String(value ?? '').replace(/[&<>"']/g, (character) => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
}[character]));
const searchable = (value) => String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
const bundleArrow = '<svg class="yumi-bundle__arrow" width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false"><path d="M4 12 12 4M4 4h8v8" stroke="currentColor" stroke-width="1.2"/></svg>';

/** A product-page configurator. Prices in the cart are always Shopify's prices. */
class YumiBundle extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    const source = this.querySelector('[data-yumi-bundle-config]');
    if (!source) return;
    try {
      this.config = JSON.parse(source.textContent);
    } catch (error) {
      console.error('[yumi-bundle] Invalid configuration', error);
      return;
    }
    this.standalone = Boolean(this.closest('[data-yumi-parade]'));
    this.controller = new AbortController();
    this.products = new Map();
    this.eligibleProductIds = new Set();
    this.catalogs = new Map();
    this.requests = new Map();
    this.hiddenControls = new Map();
    this.buttonOriginals = new Map();
    this.currency = new Intl.NumberFormat(this.config.locale || document.documentElement.lang || 'en', {
      style: 'currency', currency: this.config.currency || 'EUR',
    });
    this.initialProduct = this.rememberProduct(this.config.initial);
    if (this.initialProduct) this.eligibleProductIds.add(this.initialProduct.id);
    this.existingEligibleCount = 0;
    this.cartKnown = false;
    this.cartLoading = true;
    const initialVariant = this.variant(this.initialProduct, this.config.currentVariantId || this.config.initial?.currentVariantId);
    this.currentVariantId = initialVariant?.id;
    // The page product anchors Solo, Duo's right slot and Trio's centre slot.
    const defaults = (this.config.defaultProducts || []).map((raw) => {
      const product = this.rememberProduct(raw);
      const variant = this.variant(product, product?.currentVariantId);
      const eligible = product?.collectionIds?.some((id) => String(id) === String(this.config.eligibleCollectionId));
      if (!eligible || !variant?.available) return null;
      this.eligibleProductIds.add(product.id);
      return this.selection(product, variant);
    });
    this.offerSlots = {
      1: [initialVariant ? this.selection(this.initialProduct, initialVariant) : null],
      2: [initialVariant ? this.selection(this.initialProduct, initialVariant) : null, defaults[0] || null],
      3: [initialVariant ? this.selection(this.initialProduct, initialVariant) : null, defaults[0] || null, defaults[2] || null],
    };
    if (this.standalone) this.offerSlots = { 1: [null], 2: [null, null], 3: [null, null, null] };
    this.state = {
      size: 2,
      slots: this.offerSlots[2],
      mini: null,
      target: 0,
      kind: 'standard',
      filter: this.config.collections?.[0]?.id || null,
      query: '',
      pending: false,
      loading: false,
    };
    this.section = this.closest('.shopify-section') || this.parentElement;
    this.details = this.closest('[id^="ProductInformation-"], .product-details') || this.section;
    this.dialog = this.querySelector('[data-yumi-gallery]');
    this.findForm();
    if (this.standalone && this.dialog) {
      const offers = this.querySelector('.yumi-bundle__offers');
      this.dialog.querySelector('.yumi-bundle__toolbar')?.before(offers);
      offers?.querySelector('[data-yumi-offer="1"]')?.remove();
      this.querySelector('[data-yumi-status]') && this.dialog.querySelector('.yumi-bundle__gallery-footer')?.before(this.querySelector('[data-yumi-status]'));
      const done = this.querySelector('[data-yumi-done]');
      done?.removeAttribute('data-yumi-done');
      done?.setAttribute('data-yumi-standalone-add', '');
    }
    const { signal } = this.controller;
    this.addEventListener('click', this.onClick, { signal });
    window.addEventListener('resize', () => this.scheduleSync(), { signal });
    this.addEventListener('change', this.onChange, { signal });
    this.querySelector('[data-yumi-search]')?.addEventListener('input', this.onSearch, { signal });
    this.dialog?.addEventListener('close', this.onDialogClose, { signal });
    this.dialog?.addEventListener('cancel', this.onDialogCancel, { signal });
    this.dialog?.addEventListener('click', this.onBackdropClick, { signal });
    // Horizon delegates on:* handlers from document in capture. Window must own
    // this transaction first, otherwise its single-item handler runs before us.
    window.addEventListener('submit', this.onSubmit, { capture: true, signal });
    window.addEventListener('click', this.onNativeClick, { capture: true, signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate, { signal });
    this.section?.addEventListener(StandardEvents.productSelect, this.onProductSelect, { signal });
    this.observer = new MutationObserver((records) => {
      if (records.some((record) => !this.contains(record.target))) this.scheduleSync();
    });
    this.observer.observe(this.section, { childList: true, subtree: true });
    this.render();
    void this.refreshCartSnapshot();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
    this.observer?.disconnect();
    cancelAnimationFrame(this.syncFrame);
    clearTimeout(this.searchTimer);
    this.cancelGalleryMotion();
    if (this.dialog?.open) this.dialog.close();
    this.restoreScroll();
    for (const [button, html] of this.buttonOriginals || []) {
      if (!button.isConnected) continue;
      button.innerHTML = html;
      button.removeAttribute('data-yumi-bundle-add');
    }
    for (const [element, hidden] of this.hiddenControls || []) {
      if (element.isConnected) element.hidden = hidden;
    }
  }

  label(key, values = {}) {
    let result = String(this.config.labels?.[key] ?? '');
    for (const [name, value] of Object.entries(values)) {
      result = result.split(`[${name}]`).join(String(value)).split(`{{${name}}}`).join(String(value));
    }
    return result;
  }

  money(cents) { return this.currency.format((Number(cents) || 0) / 100); }
  name(size = this.state.size) { return this.label(['', 'solo', 'duo', 'trio'][size]); }
  selection(product, variant) { return { productId: String(product.id), variantId: String(variant.id) }; }

  rememberProduct(raw) {
    if (!raw?.id && !raw?.productId) return null;
    const next = {
      ...raw,
      id: String(raw.productId || raw.id),
      title: String(raw.title || ''),
      subtitle: String(raw.subtitle || ''),
      price: Number(raw.price) || 0,
      variants: (raw.variants || []).map((variant) => ({ ...variant, id: String(variant.id), price: Number(variant.price) || 0 })),
    };
    const product = this.products.get(next.id);
    if (product) Object.assign(product, next);
    else this.products.set(next.id, next);
    const remembered = product || next;
    if (raw.bundleEligible) this.eligibleProductIds?.add(remembered.id);
    return remembered;
  }

  variant(product, id) {
    return product?.variants.find((variant) => variant.id === String(id))
      || product?.variants.find((variant) => variant.available)
      || product?.variants[0];
  }

  resolve(selection) {
    const product = selection && this.products.get(String(selection.productId));
    const variant = product?.variants.find((item) => item.id === String(selection.variantId));
    return product && variant ? { product, variant } : null;
  }

  setSelection(index, selection) {
    this.state.slots[index] = selection;
    // Plush 1 is the page product; Plush 2 is the shared companion.
    if (this.state.size === 2) this.offerSlots[3][index] = selection;
    if (this.state.size === 3 && index !== 2) this.offerSlots[2][index] = selection;
  }

  artwork(product, alt = '') {
    if (!product?.image) return '';
    // Crop only transparent canvas margins in the SVG viewport. Original PNGs
    // remain untouched and the same asset is used in every bundle surface.
    const frames = {
      'yumi-bundle-shrek-transparent.png': [1815, 1815, 332, 189, 1149, 1510],
      'yumi-bundle-ice-spice-transparent.png': [1870, 1870, 351, 222, 1216, 1502],
      'yumi-bundle-black-hat-transparent.png': [1986, 1990, 439, 85, 1170, 1830],
    };
    const frame = Object.entries(frames).find(([filename]) => product.image.split('?')[0].endsWith('/' + filename))?.[1];
    const accessibility = alt ? 'role="img" aria-label="' + escapeHTML(alt) + '"' : 'aria-hidden="true"';
    if (!frame) return '<img class="yumi-bundle__artwork" src="' + escapeHTML(product.image) + '" alt="' + escapeHTML(alt) + '" width="260" height="300" loading="lazy">';
    const [width, height, x, y, visibleWidth, visibleHeight] = frame;
    return '<svg class="yumi-bundle__artwork" viewBox="' + [x, y, visibleWidth, visibleHeight].join(' ') + '" preserveAspectRatio="xMidYMax meet" ' + accessibility + '><image href="' + escapeHTML(product.image) + '" width="' + width + '" height="' + height + '" /></svg>';
  }

  renderOfferArtwork(button, size) {
    const host = button.querySelector('.yumi-bundle__offer-art');
    if (!host) return;
    // Composition order stays customer-first; artwork keeps the page product right/centre.
    const artworkOrder = size === 1 ? [0] : size === 2 ? [1, 0] : [1, 0, 2];
    const html = artworkOrder.map((slotIndex, index) => {
      const selection = this.offerSlots[size][slotIndex];
      const product = this.resolve(selection)?.product;
      return '<span class="yumi-bundle__offer-doll" data-doll-position="' + index + '">' + (product ? this.artwork(product) : '') + '</span>';
    }).join('');
    if (host.innerHTML !== html) host.innerHTML = html;
  }

  total(size = this.state.size) {
    const fallbackPrice = this.variant(this.initialProduct, this.currentVariantId)?.price || this.initialProduct?.price || 0;
    const raw = size === 1 ? fallbackPrice : Array.from({ length: size }, (_, index) => this.resolve(this.offerSlots[size][index])?.variant.price ?? fallbackPrice)
      .reduce((sum, price) => sum + price, 0);
    const discount = size > 1 && this.cartKnown ? this.tierDiscount(size) : 0;
    return { raw, discount: Math.min(discount, raw), total: Math.max(0, raw - discount) };
  }

  discountRate() {
    if (!this.config.baseCurrency || this.config.baseCurrency === this.config.currency) return 1;
    const currency = window.Shopify?.currency;
    const rate = Number(currency?.rate);
    return currency?.active === this.config.currency && Number.isFinite(rate) && rate > 0 ? rate : null;
  }

  tierDiscount(count) {
    const raw = count >= 3 ? Number(this.config.discounts?.trio) || 0 : count >= 2 ? Number(this.config.discounts?.duo) || 0 : 0;
    return Math.round(raw * (this.discountRate() || 0));
  }

  missingSlot() { return this.state.slots.slice(0, this.state.size).findIndex((slot) => !this.resolve(slot)); }

  findForm() {
    if (this.standalone) { this.mainComponent = null; this.form = null; return; }
    const productId = String(this.config.productId || this.initialProduct?.id || '');
    this.mainComponent = Array.from(this.details?.querySelectorAll('product-form-component[data-product-id]') || [])
      .find((component) => component.dataset.productId === productId && !component.closest('quick-add-modal, cart-drawer-component, product-card'));
    this.form = this.mainComponent?.querySelector('form[action*="/cart/add"]') || null;
  }

  scheduleSync() {
    if (this.syncFrame) return;
    this.syncFrame = requestAnimationFrame(() => {
      this.syncFrame = null;
      if (!this.isConnected) return;
      this.findForm();
      this.syncButtons();
    });
  }

  matchingSticky() {
    if (this.standalone) return [];
    const ownIds = new Set(this.initialProduct?.variants.map((variant) => variant.id));
    return Array.from(document.querySelectorAll('yumi-sticky-add-to-cart')).filter((sticky) => {
      if (sticky.mainForm === this.form) return true;
      try {
        return JSON.parse(sticky.querySelector('[data-satc-variants]')?.textContent || '[]').some((variant) => ownIds.has(String(variant.id)));
      } catch { return false; }
    });
  }

  hideNativeControl(element) {
    if (!element) return;
    if (!this.hiddenControls.has(element)) this.hiddenControls.set(element, element.hidden);
    if (!element.hidden) element.hidden = true;
  }

  syncButtons() {
    if (!this.state) return;
    const selected = this.state.slots.slice(0, this.state.size).map((slot) => this.resolve(slot));
    const unavailable = selected.some((item) => item && item.variant.available === false);
    const disabled = this.state.pending || this.cartLoading || unavailable;
    const mini = this.resolve(this.state.mini);
    const amount = this.total();
    const miniPrice = mini?.variant.price || 0;
    const price = this.money(amount.total + miniPrice);
    const compare = this.state.size > 1 && amount.discount > 0 ? this.money(amount.raw + miniPrice) : null;
    const priceHTML = `${compare ? `<s class="yumi-bundle__button-compare">${escapeHTML(compare)}</s>` : ''}<strong class="yumi-bundle__button-price">${escapeHTML(price)}</strong>`;
    const addLabel = this.label('addLabel');
    this.mainComponent?.querySelectorAll('quantity-selector-component,[ref="quantitySelectorWrapper"],.shopify-payment-button,[ref="acceleratedCheckoutButtonContainer"]')
      .forEach((element) => this.hideNativeControl(element));
    this.mainComponent?.querySelectorAll('add-to-cart-component button[type="submit"]').forEach((button) => {
      if (button.disabled !== disabled) button.disabled = disabled;
      button.setAttribute('aria-busy', String(this.state.pending || this.cartLoading));
      if (!this.buttonOriginals.has(button)) this.buttonOriginals.set(button, button.innerHTML);
      button.setAttribute('data-yumi-bundle-add', '');
      let content = button.querySelector('[data-yumi-add-content]');
      if (!content) {
        const added = button.querySelector('.add-to-cart__added');
        for (const node of Array.from(button.childNodes)) if (node !== added) node.remove();
        content = document.createElement('span');
        content.className = 'yumi-bundle__button-content';
        content.setAttribute('data-yumi-add-content', '');
        button.prepend(content);
      }
      const html = unavailable ? escapeHTML(this.label('unavailable')) : `<span>${escapeHTML(this.state.pending ? this.label('adding').toLocaleUpperCase(this.config.locale || 'en') : addLabel)}</span><span class="yumi-bundle__button-dot" aria-hidden="true">•</span><span class="add-to-cart-price yumi-bundle__button-prices">${priceHTML}</span>`;
      if (content.innerHTML !== html) content.innerHTML = html;
    });
    this.matchingSticky().forEach((sticky) => {
      const button = sticky.querySelector('[data-satc-button]');
      const priceElement = sticky.querySelector('[data-satc-price]');
      const title = sticky.querySelector('.satc__title');
      const label = sticky.querySelector('[data-satc-label]');
      if (button) {
        button.disabled = disabled;
        button.setAttribute('data-yumi-bundle-add', '');
        button.setAttribute('aria-busy', String(this.state.pending || this.cartLoading));
      }
      if (priceElement) {
        if (priceElement.innerHTML !== priceHTML) priceElement.innerHTML = priceHTML;
        priceElement.hidden = unavailable;
      }
      const separator = sticky.querySelector('[data-satc-sep]');
      if (separator) { separator.hidden = unavailable; separator.textContent = '•'; }
      if (title) title.textContent = this.state.size === 1 ? this.initialProduct.title : this.label('bundleTitle', { offer: this.name() });
      if (label) label.textContent = this.state.pending ? this.label('adding').toLocaleUpperCase(this.config.locale || 'en') : this.cartLoading ? this.label('cartLoading') : unavailable ? this.label('unavailable') : addLabel;
    });
    if (this.standalone) {
      const add = this.querySelector('[data-yumi-standalone-add]');
      if (add) {
        add.disabled = disabled;
        add.setAttribute('aria-busy', String(this.state.pending || this.cartLoading));
        add.innerHTML = this.cartLoading ? escapeHTML(this.label('cartLoading')) : escapeHTML(this.state.pending ? this.label('adding') : addLabel) + ' · ' + priceHTML + ' ' + bundleArrow;
      }
    }
    this.syncDiscoverySpacing();
  }

  syncDiscoverySpacing() {
    const note = this.querySelector('.yumi-bundle__discovery');
    const button = this.mainComponent?.querySelector('[data-yumi-bundle-add]');
    const status = this.querySelector('[data-yumi-status]');
    if (!note?.getClientRects().length || !button?.getClientRects().length || status?.textContent || this.config.mini) {
      this.style.removeProperty('--yb-discovery-offset');
      return;
    }
    const desired = parseFloat(getComputedStyle(this).getPropertyValue('--yb-discovery-gap')) || 0;
    const actual = button.getBoundingClientRect().top - note.getBoundingClientRect().bottom;
    const previous = parseFloat(this.style.getPropertyValue('--yb-discovery-offset')) || 0;
    if (Math.abs(actual - desired) > 0.5) this.style.setProperty('--yb-discovery-offset', (previous + desired - actual) + 'px');
  }

  render() {
    const composition = this.querySelector('[data-yumi-composition]');
    if (composition) composition.hidden = this.state.size === 1;
    this.querySelectorAll('[data-yumi-offer]').forEach((button) => {
      const size = Number(button.dataset.yumiOffer);
      button.setAttribute('aria-pressed', String(size === this.state.size));
      button.disabled = this.state.pending;
      this.renderOfferArtwork(button, size);
      const amount = this.total(size);
      const offerPrice = button.querySelector('[data-yumi-offer-price]');
      const compare = button.querySelector('[data-yumi-offer-compare]');
      if (offerPrice) offerPrice.textContent = this.money(amount.total);
      if (compare) { compare.textContent = this.money(amount.raw); compare.hidden = amount.discount === 0; }
      const savingsBadge = button.querySelector('[data-yumi-offer-save]');
      if (savingsBadge) {
        savingsBadge.hidden = size === 1 || amount.discount <= 0;
        const savingsLabel = savingsBadge.querySelector('[data-yumi-offer-save-label]');
        if (savingsLabel) savingsLabel.textContent = this.label('save', { amount: this.money(amount.discount) });
      }
    });
    const slots = this.querySelector('[data-yumi-slots]');
    if (slots) slots.innerHTML = this.state.slots.slice(0, this.state.size).map((slot, index) => {
      const item = this.resolve(slot);
      return `<button class="yumi-bundle__slot ${item ? 'is-filled' : ''}" type="button" data-yumi-slot="${index}" ${this.state.pending ? 'disabled' : ''} aria-haspopup="dialog" aria-controls="${escapeHTML(this.dialog?.id || '')}" aria-label="${escapeHTML(this.label(item ? 'changePlush' : 'choosePlush', { number: index + 1 }))}">
        <span class="yumi-bundle__slot-image">${item?.product.image ? this.artwork(item.product) : '<span aria-hidden="true">+</span>'}</span>
        <span class="yumi-bundle__slot-copy"><small>${escapeHTML(this.label('plushNumber', { number: index + 1 }))}</small><strong>${escapeHTML(item?.product.title || this.label('emptySlot'))}</strong><span>${escapeHTML(item ? item.product.subtitle : this.label('emptySubtitle'))}</span></span>
        <span class="yumi-bundle__slot-action">${escapeHTML(this.label(item ? 'change' : 'choosePlush', { number: index + 1 }))} ${bundleArrow}</span>
      </button>`;
    }).join('');
    const count = this.querySelector('[data-yumi-selected], [data-yumi-selected-count]');
    if (count) count.textContent = this.label('selected', { count: this.state.slots.slice(0, this.state.size).filter(Boolean).length, total: this.state.size });
    const miniHost = this.querySelector('[data-yumi-mini]');
    if (miniHost && this.config.mini) {
      const mini = this.resolve(this.state.mini);
      miniHost.innerHTML = `<button type="button" data-yumi-mini-open ${this.state.pending ? 'disabled' : ''}>${escapeHTML(mini ? `${mini.product.title} · ${this.money(mini.variant.price)}` : this.label('miniTitle'))} <span aria-hidden="true">↗</span></button>${mini ? `<button type="button" data-yumi-mini-remove ${this.state.pending ? 'disabled' : ''}>${escapeHTML(this.label('removeMini'))}</button>` : ''}`;
    }
    this.setAttribute('aria-busy', String(this.state.pending || this.cartLoading));
    this.syncButtons();
    this.renderPicked();
  }

  renderPicked() {
    const picked = this.querySelector('[data-yumi-gallery-picked]');
    const focusedSlot = picked?.contains(document.activeElement) ? document.activeElement.dataset.yumiPickedSlot : null;
    if (picked) picked.innerHTML = this.state.slots.slice(0, this.state.size).map((slot, index) => {
      const item = this.resolve(slot);
      return `<button type="button" class="yumi-bundle__picked ${this.state.kind === 'standard' && index === this.state.target ? 'is-current' : ''}" data-yumi-picked-slot="${index}" aria-pressed="${this.state.kind === 'standard' && index === this.state.target}" aria-label="${escapeHTML(this.label('choosePlush', { number: index + 1 }))}">${item?.product.image ? this.artwork(item.product) : '+'}<small>${index + 1}</small></button>`;
    }).join('');
    if (focusedSlot != null) picked.querySelector('[data-yumi-picked-slot="' + focusedSlot + '"]')?.focus({ preventScroll: true });
    const ordinal = this.label(['first', 'second', 'third'][this.state.target]);
    const headingValues = { ordinal, offer: this.name().toUpperCase(), number: this.state.target + 1, total: this.state.size };
    const title = this.querySelector('[data-yumi-gallery-title]');
    if (title) title.textContent = this.state.kind === 'mini' ? this.label('miniTitle') : this.label('galleryTitle', headingValues);
    const step = this.querySelector('[data-yumi-gallery-step]');
    if (step) step.textContent = this.label('galleryStep', headingValues);
  }

  setStatus(text = '') {
    const status = this.querySelector('[data-yumi-status], [data-yumi-error]');
    if (status) status.textContent = text;
  }

  collection(id = this.state.filter, kind = this.state.kind) {
    if (kind === 'mini') return this.config.mini;
    return this.config.collections?.find((collection) => String(collection.id) === String(id));
  }

  async loadCollection(id = this.state.filter, allPages = false, kind = this.state.kind) {
    const collection = this.collection(id, kind);
    if (!collection?.url) {
      this.galleryError = this.label('noCollections');
      this.renderGallery();
      return;
    }
    const key = kind === 'mini' ? `mini:${id}` : String(id);
    let catalog = this.catalogs.get(key);
    if (catalog?.loaded && (!allPages || !catalog.nextPage)) {
      if (this.state.filter === id && this.state.kind === kind) this.state.loading = this.requests.has(key);
      this.renderGallery(); return;
    }
    if (this.requests.has(key)) {
      await this.requests.get(key);
      if (allPages && this.catalogs.get(key)?.nextPage) return this.loadCollection(id, true, kind);
      return;
    }
    const request = (async () => {
      if (this.state.filter === id && this.state.kind === kind) {
        this.state.loading = true;
        this.galleryError = '';
        this.renderGallery();
      }
      try {
        do {
          catalog = this.catalogs.get(key) || { products: [], page: 0, nextPage: 1, total: 0 };
          const url = new URL(collection.url, location.origin);
          url.searchParams.set('section_id', this.config.catalogSectionId || 'yumi-bundle-catalog');
          url.searchParams.set('page', String(catalog.nextPage || 1));
          const response = await fetch(url, { headers: { Accept: 'text/html' }, credentials: 'same-origin', signal: this.controller.signal });
          if (!response.ok) throw new Error(`Catalog HTTP ${response.status}`);
          const document = new DOMParser().parseFromString(await response.text(), 'text/html');
          const data = JSON.parse(document.querySelector('[data-yumi-catalog-json]')?.textContent || 'null');
          if (!Array.isArray(data?.products)) throw new Error('Invalid catalog response');
          if (catalog.page && Number(data.page) <= catalog.page) throw new Error('Catalog pagination did not advance');
          const eligibleProducts = data.products.filter((product) => {
            const eligible = !this.config.eligibleCollectionId || (product.collectionIds || []).some((collectionId) => String(collectionId) === String(this.config.eligibleCollectionId));
            return kind === 'mini' ? !eligible : eligible;
          });
          const remembered = eligibleProducts.map((product) => this.rememberProduct({ ...product, bundleEligible: kind !== 'mini' })).filter(Boolean);
          const unique = new Map([...catalog.products, ...remembered].map((product) => [product.id, product]));
          catalog = {
            products: [...unique.values()], page: Number(data.page) || 1, pages: Number(data.pages) || 1,
            nextPage: data.nextPage ? Number(data.nextPage) : null,
            total: unique.size, loaded: true,
          };
          this.catalogs.set(key, catalog);
          if (this.state.filter === id && this.state.kind === kind) this.renderGallery();
        } while (allPages && catalog.nextPage && this.controller && !this.controller.signal.aborted);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.warn('[yumi-bundle] Catalog could not be loaded', error);
          if (this.state.filter === id && this.state.kind === kind) this.galleryError = this.label('loadError');
        }
      } finally {
        this.requests.delete(key);
        if (this.state.filter === id && this.state.kind === kind) {
          this.state.loading = false;
          this.renderGallery();
        }
      }
    })();
    this.requests.set(key, request);
    await request;
  }

  async loadMore() {
    const key = this.state.kind === 'mini' ? `mini:${this.state.filter}` : String(this.state.filter);
    const catalog = this.catalogs.get(key);
    if (catalog) catalog.loaded = false;
    await this.loadCollection(this.state.filter, Boolean(this.state.query.trim()));
  }

  renderGallery() {
    if (!this.state || !this.dialog) return;
    const key = this.state.kind === 'mini' ? `mini:${this.state.filter}` : String(this.state.filter);
    const catalog = this.catalogs.get(key);
    const query = searchable(this.state.query.trim());
    const products = (catalog?.products || []).filter((product) => searchable(`${product.title} ${product.subtitle}`).includes(query));
    const active = this.state.kind === 'mini' ? this.state.mini : this.state.slots[this.state.target];
    const results = this.querySelector('[data-yumi-results]');
    if (results) results.innerHTML = products.length ? products.map((product) => {
      const chosen = active?.productId === product.id;
      const variant = this.variant(product, chosen ? active.variantId : null);
      const options = product.variants.length > 1 ? `<label class="yumi-bundle__variant"><span>${escapeHTML(this.label('variantLabel'))}</span><select data-yumi-variant-select="${escapeHTML(product.id)}" aria-label="${escapeHTML(this.label('variantLabel'))}">${product.variants.map((option) => `<option value="${escapeHTML(option.id)}" ${option.id === variant?.id ? 'selected' : ''} ${option.available ? '' : 'disabled'}>${escapeHTML(option.title)}${option.available ? '' : ` — ${escapeHTML(this.label('unavailable'))}`}</option>`).join('')}</select></label>` : '';
      return `<article class="yumi-bundle__model"><button type="button" class="yumi-bundle__model-choice" data-yumi-model="${escapeHTML(product.id)}" data-yumi-variant="${escapeHTML(variant?.id || '')}" aria-pressed="${chosen}" ${variant?.available ? '' : 'disabled'}><span class="yumi-bundle__model-image">${product.image ? this.artwork(product, product.title) : ''}</span><strong>${escapeHTML(product.title)}</strong><span class="yumi-bundle__model-subtitle">${escapeHTML(product.subtitle)}</span>${variant?.available ? '' : `<span class="yumi-bundle__model-unavailable">${escapeHTML(this.label('unavailable'))}</span>`}</button>${options}</article>`;
    }).join('') : `<p class="yumi-bundle__empty">${escapeHTML(this.galleryError || this.label(this.state.loading ? 'loading' : 'noResults'))}</p>`;
    const count = this.querySelector('[data-yumi-count]');
    if (count) count.textContent = this.galleryError || this.label('modelCount', { count: query ? products.length : catalog?.total || products.length });
    const more = this.querySelector('[data-yumi-load-more]');
    if (more) {
      more.hidden = !catalog?.nextPage && !this.galleryError;
      more.disabled = this.state.loading;
      more.textContent = this.label(this.state.loading ? 'loading' : 'loadMore');
    }
    this.querySelectorAll('[data-yumi-filter]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.yumiFilter === String(this.state.filter)));
    });
    this.renderPicked();
  }

  prefersReducedMotion() {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
  }

  animateSurface(element, frames, duration, extra = {}) {
    if (!element?.animate || this.prefersReducedMotion()) return Promise.resolve();
    try {
      const animation = element.animate(frames, {
        duration, easing: 'cubic-bezier(.22,1,.36,1)', fill: 'both', ...extra,
      });
      this.galleryAnimations ||= new Set();
      this.galleryAnimations.add(animation);
      return animation.finished.catch(() => {}).finally(() => {
        this.galleryAnimations?.delete(animation);
        animation.cancel();
      });
    } catch {
      // Older browsers can omit pseudo-element animations without blocking UI.
      return Promise.resolve();
    }
  }

  cancelGalleryMotion() {
    this.galleryMotionVersion = (this.galleryMotionVersion || 0) + 1;
    for (const animation of this.galleryAnimations || []) animation.cancel();
    this.galleryAnimations?.clear();
    this.galleryClosing = false;
    this.galleryClosePromise = null;
    this.dialog?.removeAttribute('data-yumi-closing');
  }

  async closeGallery() {
    if (!this.dialog?.open) return;
    if (this.galleryClosing) return this.galleryClosePromise;
    const current = getComputedStyle(this.dialog);
    const firstFrame = { opacity: current.opacity, transform: current.transform };
    this.cancelGalleryMotion();
    const version = this.galleryMotionVersion;
    this.galleryClosing = true;
    this.dialog.setAttribute('data-yumi-closing', '');
    this.galleryClosePromise = Promise.all([
      this.animateSurface(this.dialog, [firstFrame, { opacity: 0, transform: 'translateY(12px)' }], 180),
      this.animateSurface(this.dialog, [{ opacity: 1 }, { opacity: 0 }], 180, { pseudoElement: '::backdrop' }),
    ]).then(() => {
      if (version !== this.galleryMotionVersion) return;
      if (this.dialog.open) this.dialog.close();
      this.galleryClosing = false;
      this.dialog.removeAttribute('data-yumi-closing');
    });
    return this.galleryClosePromise;
  }

  async changeGalleryStep(target, kind = this.state.kind) {
    if (!this.dialog?.open || this.galleryClosing || this.state.pending) return;
    const previous = this.state.target;
    this.cancelGalleryMotion();
    const version = this.galleryMotionVersion;
    if (target === previous && kind === this.state.kind) { this.renderGallery(); return; }
    const direction = target > previous ? 1 : -1;
    const surfaces = [
      this.querySelector('.yumi-bundle__gallery-header > div'),
      this.querySelector('.yumi-bundle__gallery-scroll'),
    ];
    await Promise.all(surfaces.map(element => this.animateSurface(element, [
      { opacity: 1, transform: 'translateX(0)' },
      { opacity: 0, transform: 'translateX(' + (-10 * direction) + 'px)' },
    ], 100)));
    if (version !== this.galleryMotionVersion || !this.dialog.open) return;
    this.state.target = target;
    this.state.kind = kind;
    this.renderGallery();
    await Promise.all(surfaces.map(element => this.animateSurface(element, [
      { opacity: 0, transform: 'translateX(' + (10 * direction) + 'px)' },
      { opacity: 1, transform: 'translateX(0)' },
    ], 160)));
  }

  openFromParade(raw, size = 2) {
    if (!this.standalone || this.state.pending) return false;
    const nextSize = Number(size) === 3 ? 3 : 2;
    this.state.size = nextSize;
    this.state.slots = this.offerSlots[nextSize];
    if (raw) {
      if (!raw.collectionIds?.some(id => String(id) === String(this.config.eligibleCollectionId))) return false;
      const product = this.rememberProduct(raw);
      const variant = this.variant(product, raw.currentVariantId);
      if (!variant?.available) return false;
      this.eligibleProductIds.add(product.id);
      this.setSelection(0, this.selection(product, variant));
    }
    this.setStatus(); this.render();
    this.openGallery(Math.max(0, this.missingSlot()));
    return true;
  }

  openGallery(target = 0, kind = 'standard') {
    if (!this.dialog || this.state.pending) return;
    this.cancelGalleryMotion();
    if (!this.dialog.open) this.returnFocus = document.activeElement;
    this.state.target = target;
    this.state.kind = kind;
    this.state.query = '';
    this.state.filter = kind === 'mini' ? this.config.mini?.id : this.config.collections?.[0]?.id;
    this.galleryError = '';
    const search = this.querySelector('[data-yumi-search]');
    if (search) search.value = '';
    const filters = this.querySelector('.yumi-bundle__filters');
    if (filters) filters.hidden = kind === 'mini';
    this.querySelectorAll('[data-yumi-filter]').forEach((button) => { button.hidden = kind === 'mini'; });
    this.renderGallery();
    if (!this.dialog.open) {
      this.previousOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      this.dialog.showModal();
      void this.animateSurface(this.dialog, [
        { opacity: 0, transform: 'translateY(18px)' },
        { opacity: 1, transform: 'translateY(0)' },
      ], 260);
      void this.animateSurface(this.dialog, [{ opacity: 0 }, { opacity: 1 }], 220, { pseudoElement: '::backdrop' });
    }
    this.querySelector('[data-yumi-close]')?.focus({ preventScroll: true });
    this.querySelector('.yumi-bundle__gallery-scroll')?.scrollTo({ top: 0 });
    void this.loadCollection();
  }

  restoreScroll() {
    if (this.previousOverflow !== undefined) {
      document.documentElement.style.overflow = this.previousOverflow;
      this.previousOverflow = undefined;
    }
  }

  onDialogClose = () => {
    this.cancelGalleryMotion();
    this.restoreScroll();
    const focusTarget = this.returnFocus?.isConnected ? this.returnFocus : this.querySelector('[data-yumi-slot="' + this.state.target + '"]');
    focusTarget?.focus?.({ preventScroll: true });
  };

  onDialogCancel = (event) => {
    event.preventDefault();
    void this.closeGallery();
  };

  onBackdropClick = (event) => {
    if (event.target !== this.dialog) return;
    const rect = this.dialog.getBoundingClientRect();
    if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) void this.closeGallery();
  };

  onClick = (event) => {
    const button = event.target.closest('button');
    if (!button || !this.contains(button)) return;
    if (button.hasAttribute('data-yumi-close') || button.hasAttribute('data-yumi-done')) { void this.closeGallery(); return; }
    if (this.state.pending || this.galleryClosing) return;
    if (button.hasAttribute('data-yumi-standalone-add')) { void this.addToCart(); return; }
    if (button.hasAttribute('data-yumi-offer')) {
      this.state.size = Math.max(1, Math.min(3, Number(button.dataset.yumiOffer) || 1));
      this.state.slots = this.offerSlots[this.state.size];
      if (this.state.size === 1) {
        const variant = this.variant(this.initialProduct, this.currentVariantId);
        this.state.slots[0] = variant ? this.selection(this.initialProduct, variant) : null;
      }
      this.setStatus(); this.render();
      if (this.standalone) { this.state.target = Math.min(this.state.target, this.state.size - 1); this.renderGallery(); }
    } else if (button.hasAttribute('data-yumi-slot')) {
      this.openGallery(Number(button.dataset.yumiSlot));
    } else if (button.hasAttribute('data-yumi-picked-slot')) {
      if (this.state.kind === 'mini') this.openGallery(Number(button.dataset.yumiPickedSlot));
      else void this.changeGalleryStep(Number(button.dataset.yumiPickedSlot));
    } else if (button.hasAttribute('data-yumi-filter')) {
      this.state.filter = button.dataset.yumiFilter;
      this.galleryError = ''; this.renderGallery(); void this.loadCollection(this.state.filter, Boolean(this.state.query.trim()));
    } else if (button.hasAttribute('data-yumi-load-more')) {
      void this.loadMore();
    } else if (button.hasAttribute('data-yumi-mini-open')) {
      if (this.config.mini) this.openGallery(0, 'mini');
    } else if (button.hasAttribute('data-yumi-mini-remove')) {
      this.state.mini = null; this.render();
    } else if (button.hasAttribute('data-yumi-model')) {
      const product = this.products.get(button.dataset.yumiModel);
      const variant = product?.variants.find((item) => item.id === button.dataset.yumiVariant);
      if (!variant?.available) { this.setStatus(this.label('unavailable')); return; }
      if (this.state.kind === 'mini') this.state.mini = this.selection(product, variant);
      else this.setSelection(this.state.target, this.selection(product, variant));
      this.setStatus(); this.render();
      const missing = this.missingSlot();
      if (this.state.kind === 'standard' && missing !== -1) {
        void this.changeGalleryStep(missing);
      } else if (this.standalone) this.renderGallery();
      else void this.closeGallery();
    }
  };

  onChange = (event) => {
    const select = event.target.closest('[data-yumi-variant-select]');
    if (!select) return;
    const card = select.closest('.yumi-bundle__model');
    const button = card?.querySelector('[data-yumi-model]');
    const product = this.products.get(select.dataset.yumiVariantSelect);
    const variant = product?.variants.find((item) => item.id === select.value);
    if (button && variant) {
      button.dataset.yumiVariant = variant.id;
      button.disabled = !variant.available;
    }
  };

  onSearch = (event) => {
    this.state.query = event.target.value;
    this.renderGallery();
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => {
      if (this.state.query.trim()) void this.loadCollection(this.state.filter, true);
    }, 250);
  };

  onProductSelect = async (event) => {
    if (event.target.closest?.('product-card, quick-add-modal')) return;
    try {
      const result = await event.promise;
      if (!this.isConnected) return;
      const detail = result?.detail;
      if (detail?.productId && String(detail.productId) !== String(this.initialProduct?.id)) return;
      const resource = detail?.resource;
      if (resource && this.initialProduct?.variants.some((variant) => variant.id === String(resource.id))) {
        const variant = this.initialProduct.variants.find((item) => item.id === String(resource.id));
        variant.available = resource.available !== false;
        if (Number.isFinite(Number(resource.price))) variant.price = Number(resource.price);
        this.currentVariantId = variant.id;
        this.offerSlots[1][0] = this.selection(this.initialProduct, variant);
        for (const [size, index] of [[2, 0], [3, 0]]) {
          if (this.offerSlots[size][index]?.productId === this.initialProduct.id) {
            this.offerSlots[size][index] = this.selection(this.initialProduct, variant);
          }
        }
      }
      this.findForm(); this.render(); this.scheduleSync();
    } catch (error) {
      if (error?.name !== 'AbortError') console.warn('[yumi-bundle] Product selection could not be synchronized', error);
    }
  };

  ownsForm(form) {
    return form === this.form || this.matchingSticky().some((sticky) => sticky.querySelector('.satc__form') === form);
  }

  onNativeClick = (event) => {
    const button = event.target.closest?.('button[type="submit"]');
    if (!button) return;
    const mainButton = this.mainComponent?.contains(button) && button.matches('add-to-cart-component button[ref="addToCartButton"]');
    const stickyButton = this.matchingSticky().some((sticky) => sticky.contains(button) && button.hasAttribute('data-satc-button'));
    if (!mainButton && !stickyButton) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const missing = this.missingSlot();
    if (this.state.pending || this.cartLoading || missing !== -1) {
      if (missing !== -1 && !this.state.pending && !this.cartLoading) this.requireSelection(missing);
      return;
    }
    void this.addToCart();
  };

  onSubmit = (event) => {
    if (!this.ownsForm(event.target)) return;
    event.preventDefault(); event.stopImmediatePropagation();
    void this.addToCart();
  };

  requireSelection(missing) {
    this.setStatus(this.label('incomplete', { count: this.state.slots.slice(0, this.state.size).filter((slot) => !this.resolve(slot)).length }));
    this.openGallery(missing);
  }

  async fetchCart() {
    const component = document.querySelector('cart-items-component');
    if (typeof component?.fetchCartData === 'function') return component.fetchCartData();
    const cartUrl = window.Theme?.routes?.cart_url || `${window.Shopify?.routes?.root || '/'}cart`;
    const response = await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
    if (!response.ok) throw new Error(`Cart HTTP ${response.status}`);
    return response.json();
  }

  async ensureEligibility() {
    if (this.eligibilityComplete) return;
    const all = this.config.collections?.find((collection) => collection.id === 'all');
    if (!all) throw new Error('Eligible collection is unavailable');
    await this.loadCollection(all.id, true, 'standard');
    const catalog = this.catalogs.get(String(all.id));
    if (!catalog?.loaded || catalog.nextPage) throw new Error('Eligible collection could not be completely loaded');
    this.eligibilityComplete = true;
  }

  async refreshCartSnapshot(cartPromise) {
    const generation = (this.cartSnapshotGeneration || 0) + 1;
    this.cartSnapshotGeneration = generation;
    this.cartLoading = true;
    this.render();
    try {
      const cart = await (cartPromise || this.fetchCart());
      if (!Array.isArray(cart?.items)) throw new Error('Invalid cart response');
      if (!this.eligibilityComplete && cart.items.some((item) => !this.eligibleProductIds.has(String(item.product_id)))) {
        await this.ensureEligibility();
      }
      if (generation !== this.cartSnapshotGeneration) return;
      this.existingEligibleCount = cart.items.reduce((count, item) => count + (this.eligibleProductIds.has(String(item.product_id)) ? Number(item.quantity) || 0 : 0), 0);
      this.cartKnown = true;
    } catch (error) {
      if (generation !== this.cartSnapshotGeneration) return;
      this.cartKnown = false;
      console.warn('[yumi-bundle] Cart prices could not be confirmed', error);
    } finally {
      if (generation === this.cartSnapshotGeneration) {
        this.cartLoading = false;
        if (this.isConnected) this.render();
      }
    }
  }

  onCartUpdate = (event) => {
    const cart = event.promise
      ? event.promise.then(({ detail }) => Array.isArray(detail?.items) ? { items: detail.items } : this.fetchCart()).catch(() => this.fetchCart())
      : this.fetchCart();
    void this.refreshCartSnapshot(cart);
  };

  async addToCart() {
    if (this.state.pending || this.cartLoading) return;
    const missing = this.missingSlot();
    if (missing !== -1) { this.requireSelection(missing); return; }
    const selected = this.state.slots.slice(0, this.state.size).map((slot) => this.resolve(slot));
    const mini = this.resolve(this.state.mini);
    if (mini) selected.push(mini);
    if (selected.some((item) => !item?.variant.available)) { this.setStatus(this.label('unavailable')); return; }
    const quantities = new Map();
    selected.slice(0, this.state.size).forEach(({ variant }) => quantities.set(variant.id, (quantities.get(variant.id) || 0) + 1));
    const items = [...quantities].map(([id, quantity]) => ({
      id: Number(id), quantity,
      ...(this.state.size > 1 ? { properties: { _yumi_offer: String(this.state.size) } } : {}),
    }));
    if (mini) items.push({ id: Number(mini.variant.id), quantity: 1 });
    const sectionIds = [...new Set(Array.from(document.querySelectorAll('cart-items-component[data-section-id]')).map((element) => element.dataset.sectionId).filter(Boolean))];
    const deferred = CartLinesUpdateEvent.createPromise();
    // Native drawer, counters, and cart sections subscribe to this one transaction.
    this.state.pending = true;
    this.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add', context: 'product',
      lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
      promise: deferred.promise,
    }));
    this.setStatus(); this.render();
    let didAdd = false;
    try {
      const addUrl = window.Theme?.routes?.cart_add_url || this.form?.action || `${window.Shopify?.routes?.root || '/'}cart/add.js`;
      const response = await fetch(addUrl, {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(','), sections_url: `${location.pathname}${location.search}` }),
      });
      const data = await response.json();
      if (!response.ok || data.status) {
        const error = new Error(typeof data.description === 'string' ? data.description : data.message || this.label('addError'));
        error.shopifyError = data;
        throw error;
      }
      didAdd = true;
      if (this.standalone) await this.closeGallery();
      const cart = await this.fetchCart();
      await this.refreshCartSnapshot(Promise.resolve(cart));
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'yumi-bundle', sourceId: this.id || `yumi-bundle-${this.config.sectionId}`, itemCount: selected.length, productId: String(this.config.productId), sections: data.sections, didError: false },
      });
      this.setStatus();
    } catch (error) {
      this.dispatchEvent(new CartErrorEvent({ error: error.message || this.label('addError'), code: error.shopifyError ? 'INVALID' : 'SERVICE_UNAVAILABLE', detail: error.shopifyError || {} }));
      if (didAdd) {
        deferred.reject(error);
        this.setStatus(this.label('cartRefreshError'));
      } else {
        try {
          const cart = await this.fetchCart();
          await this.refreshCartSnapshot(Promise.resolve(cart));
          deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { items: cart.items, source: 'yumi-bundle', sourceId: this.id, didError: true } });
        } catch (refreshError) { deferred.reject(refreshError); }
        this.setStatus([error.shopifyError ? error.message : '', this.label('addReviewError')].filter(Boolean).join(' '));
      }
      console.warn('[yumi-bundle] Cart transaction could not complete', error);
    } finally {
      this.state.pending = false;
      if (this.isConnected) this.render();
    }
  }
}

if (!customElements.get('yumi-bundle')) customElements.define('yumi-bundle', YumiBundle);




